import add from "@/assets/add.svg";
import Button from "@/common/elements/Button";
import { INPUT_VARIANT } from "@/common/elements/InputElements/const";
import {
  InmateDetailsTableContainer,
  TableHeader,
} from "@/components/InmateDetails/styles";
import { useEffect, useState } from "react";
import SkillsCertificationsModal from "./SkillsCertificationsModal";
import DataTable from "@/common/elements/DataTable";
import { StyledFlex } from "@/common/styles";
import { useDispatch, useSelector } from "react-redux";
import { isNonEmptyVariable } from "@/util/common_function";
import _ from "lodash";
import { v4 as uuidV4 } from "uuid";
import Popconfirm from "@/common/elements/Popconfirm";
import API_ENDPOINTS from "@/util/api";
import { setBookings, setDeleteAPis } from "@/slices/bookingsSlice";
import dayjs from "dayjs";
import useGridColumnConfig from "@/hooks/useGridColumnConfig";

const SkillsTableHeader = [
  { key: "id", text: "#" },
  { key: "skillName", text: "Skill Name" },
  { key: "skillLevel", text: "Skill Level" },
  { key: "trainingName", text: "Training Name" },
  { key: "scheduledDate", text: "Scheduled Date" },
  { key: "completedDate", text: "Completed Date" },
  { key: "certificationName", text: "Certification Name" },
  { key: "certificationDate", text: "Certification Date" },
];

const SkillsCertifications = (startingPoint = "") => {
  const dispatch = useDispatch();
  const { bookings, bookingsFieldsEnabled } = useSelector((s) => s?.bookings);
  const [isModalShown, setIsModalShown] = useState(false);
  const [skillList, setSkillList] = useState([]);
  const [selectSkill, setSelectSkill] = useState(null);

  // "Config" — drag-reorder/resize columns, persisted per-user (see
  // docs/plans/grid-column-config-feature.md). This tab is only reachable
  // from the full Staff module, so the gridId is hardcoded the same way
  // Bookings' grids are, rather than threaded through startingPoint.
  const {
    headers: skillsHeaders,
    isConfiguring,
    configButtonLabel,
    startConfiguring,
    confirmSave,
    discardChanges,
    dataTableRef: datatableRef,
  } = useGridColumnConfig("Staff:Skills", SkillsTableHeader);

  useEffect(() => {
    if (isNonEmptyVariable(bookings)) {
      const skill = bookings?.skills?.map((s, index) => {
        return {
          ...s,
          id: index + 1,
          uuid: uuidV4(),
        };
      });
      setSkillList(() => skill||[]);
    } else {
      setSkillList(() => []);
    }
  }, [bookings]);

  const onRowClick = (value, index) => {
    if (isNonEmptyVariable(value)) {
      const data = {
        ...value,
        scheduledDate: isNonEmptyVariable(value?.scheduledDate)
          ? dayjs(value?.scheduledDate)
          : null,
        completedDate: isNonEmptyVariable(value?.completedDate)
          ? dayjs(value?.completedDate)
          : null,
        certificationDate: isNonEmptyVariable(value?.certificationDate)
          ? dayjs(value?.certificationDate)
          : null,
      };
      setSelectSkill(() => data);
      if(index !== null && index !== undefined) {
        onEditClick();
      }
    }
  };

  const onAddClick = () => {
    setSelectSkill(() => null);
    setIsModalShown(true);
  };

  const onEditClick = () => {
    setIsModalShown(true);
  };

  const handleSubmit = (values) => {
    let skillListData = _.cloneDeep(skillList);
    if (isNonEmptyVariable(selectSkill)) {
      skillListData = skillListData?.map((m) => {
        if (m?.uuid === selectSkill?.uuid) {
          return {
            ...m,
            ...values,
          };
        }

        return m;
      });
    } else {
      skillListData?.push(values);
    }

    setSkillList(() => skillListData);
    dispatch(
      setBookings({
        skills: skillListData,
      })
    );
    handleCancel();
  };

  const handleCancel = () => {
    setSelectSkill(() => null);
    setIsModalShown(false);
    datatableRef?.current?.resetAll();
  };

  const handleDelete = () => {
    let skillListData = _.cloneDeep(skillList);
    if (isNonEmptyVariable(selectSkill)) {
      skillListData = skillListData?.filter(
        (m) => m?.uuid !== selectSkill?.uuid
      );

      if (isNonEmptyVariable(selectSkill?.skillId)) {
        const url = API_ENDPOINTS.staff.skill.delete + selectSkill?.skillId;
        dispatch(setDeleteAPis(url));
      }
    }

    setSkillList(() => skillListData);
    dispatch(
      setBookings({
        skills: skillListData,
      })
    );

    handleCancel();
  };

  return (
    <>
      <InmateDetailsTableContainer>
        <TableHeader>
          <h6>Skills, Trainings & Certifications</h6>
          <StyledFlex gap="10px">
            <Button
              color="primary"
              type="primary"
              className="add-btn"
              onClick={onAddClick}
              disabled={!bookingsFieldsEnabled || isConfiguring}
            >
              Add
            </Button>
            <Button
              color="primary"
              type="primary"
              onClick={onEditClick}
              disabled={selectSkill === null || isConfiguring}
            >
              Details
            </Button>
            <Popconfirm
              title="Delete the skill"
              description="Are you sure to delete this skill record?"
              onConfirm={handleDelete}
              onCancel={() => { }}
              okText="Yes"
              cancelText="No"
            >
              <Button
                color="primary"
                type="primary"
                disabled={selectSkill === null || !bookingsFieldsEnabled || isConfiguring}
              >
                Delete
              </Button>
            </Popconfirm>
            <Button
              color="primary"
              type="primary"
              disabled={selectSkill === null || isConfiguring}
              onClick={handleCancel}
            >
              Cancel
            </Button>

            {/* Config — drag-reorder/resize columns */}
            {!isConfiguring ? (
              <Button htmlType="button" onClick={startConfiguring}>
                {configButtonLabel}
              </Button>
            ) : (
              <Popconfirm
                title="Do you want to save the current list view configuration?"
                onConfirm={confirmSave}
                onCancel={discardChanges}
                okText="Yes"
                cancelText="No"
              >
                <Button htmlType="button">{configButtonLabel}</Button>
              </Popconfirm>
            )}
          </StyledFlex>
        </TableHeader>
        <DataTable
          ref={datatableRef}
          rowUniqueId="uuid"
          headers={skillsHeaders}
          draggable={isConfiguring}
          data={skillList||[]}
          onRowClick={onRowClick}
          selected={selectSkill === null}
          allowTableSorting={true}
        />
      </InmateDetailsTableContainer>
      <SkillsCertificationsModal
        show={isModalShown}
        handleCancel={handleCancel}
        formSubmit={handleSubmit}
        selectSkill={selectSkill}
        bookingsFieldsEnabled={bookingsFieldsEnabled}
        startingPoint={startingPoint}
      />
    </>
  );
};

export default SkillsCertifications;
